import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, PhoneCall, ShieldCheck, MapPin, BarChart3,
  Users, Target, Zap, AlertCircle, Loader2, Award,
  Home as HomeIcon, Plane, Clock, TrendingUp, LineChart, Search,
  Wrench, Droplets, Flame, Sun, Bug, Hammer, Wind, Paintbrush, Truck,
  Package, DoorOpen, Bath, ThermometerSun, Cog, ScrollText,
  Hotel, Ship, Compass, Palmtree, Luggage, Sparkles, Rocket,
} from "lucide-react";
import Header from "../components/Header";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// --------------------------------------------------------------------------- //
// Static content                                                              //
// --------------------------------------------------------------------------- //

const PLANS = [
  {
    id: "starter", name: "Starter", price: 300,
    tag: "Solo operators & single-service shops",
    features: [
      "1 Google Search Ads campaign",
      "1 high-converting landing page + call tracking",
      "Recommended ad spend: $500 – $1,000/mo",
      "Weekly performance report + insights",
      "Email support (24-hr response)",
      "Keyword research + negative-keyword hygiene",
    ],
    highlight: false,
  },
  {
    id: "growth", name: "Growth", price: 500,
    tag: "Most chosen — for scaling home-service pros",
    features: [
      "2 Google Ads + 1 Meta Ads campaign",
      "2 landing pages + call tracking + call recording",
      "Recommended ad spend: $1,500 – $3,000/mo",
      "Bi-weekly strategy call with your manager",
      "Twice-weekly reports + Slack/WhatsApp updates",
      "Smart bidding, audience layering, day-parting",
      "Priority support (same-business-day response)",
    ],
    highlight: true,
  },
  {
    id: "pro", name: "Pro", price: 1000,
    tag: "Multi-location, franchise & travel brands",
    features: [
      "Unlimited Google + Meta + YouTube campaigns",
      "Up to 5 landing pages + multi-line call tracking",
      "Recommended ad spend: $5,000 – $25,000/mo",
      "Weekly strategy call + monthly business review",
      "Daily reports + custom Looker Studio dashboard",
      "Full-funnel optimization (ads → LP → call → close)",
      "Dedicated account manager + creative team",
    ],
    highlight: false,
  },
];

const HOW_IT_WORKS = [
  { icon: Users, num: "01", title: "Free 20-min consultation", body: "We learn your business, service area, current marketing, average ticket size, and target cost-per-lead." },
  { icon: Target, num: "02", title: "Campaign build (5–7 days)", body: "Keyword research, ad copy, landing page, call tracking, conversion pixels — engineered for your market." },
  { icon: BarChart3, num: "03", title: "Optimize & scale", body: "Daily monitoring, weekly refinement, monthly strategy. You always see what you spent and what it earned." },
];

// Home services with per-vertical icons
const VERTICALS_HOME = [
  { name: "HVAC", icon: ThermometerSun },
  { name: "Plumbing", icon: Droplets },
  { name: "Electricians", icon: Zap },
  { name: "Roofing", icon: HomeIcon },
  { name: "Water & Fire Damage", icon: Flame },
  { name: "Solar", icon: Sun },
  { name: "Garage Doors", icon: DoorOpen },
  { name: "Pest Control", icon: Bug },
  { name: "Bathroom Remodel", icon: Bath },
  { name: "Windows & Doors", icon: DoorOpen },
  { name: "Painting", icon: Paintbrush },
  { name: "Junk Removal", icon: Truck },
  { name: "Movers", icon: Package },
  { name: "Appliance Repair", icon: Cog },
  { name: "Chimney", icon: Wind },
  { name: "Handyman", icon: Hammer },
];

const VERTICALS_TRAVEL = [
  { name: "Flight Booking", icon: Plane },
  { name: "Hotels & Resorts", icon: Hotel },
  { name: "Cruise Lines", icon: Ship },
  { name: "Travel Insurance", icon: ShieldCheck },
  { name: "Vacation Rentals", icon: Palmtree },
  { name: "Tour & Adventure", icon: Compass },
  { name: "Luggage & Gear", icon: Luggage },
  { name: "Concierge Services", icon: ScrollText },
];

const TRAFFIC_SOURCES = [
  { name: "Google Search Ads", desc: "Reach customers the second they search — highest-intent traffic on the planet." },
  { name: "Google Local Services", desc: "Pay-per-lead placements at the very top of local search with the Google Guarantee badge." },
  { name: "Google Display + YouTube", desc: "Stay top-of-mind with retargeting and video that follows warm prospects around the web." },
  { name: "Meta (Facebook + Instagram)", desc: "Demand-generation, lookalike audiences, and lead forms tuned for local service buyers." },
];

const DELIVERABLES = [
  "Deep keyword research aligned to your service area & profit margins",
  "Ad copy written for maximum Quality Score, CTR, and phone-call intent",
  "Landing page built to convert — mobile-first, fast, TCPA-compliant",
  "Call tracking + call recording — hear every lead your ads produced",
  "Weekly optimization: bids, keywords, audiences, negatives, ad copy",
  "Transparent reporting: what you spent, what you got, what happens next",
];

const FAQS = [
  { q: "How much ad spend should I budget?", a: "We recommend a minimum of $500/month in ad spend on top of the management fee. Realistic budgets vary by service area and competition — we'll advise on a specific range during the free consultation. Ad spend is billed directly by Google/Meta, not by us." },
  { q: "Do you guarantee a specific number of leads?", a: "No, and be cautious of any agency that does. Results depend on your budget, market, competition, seasonality, and your team's speed to answer the phone. We commit to running your campaigns to industry best practices and being fully transparent about every dollar." },
  { q: "What's included in the management fee?", a: "Campaign setup, ad copy, landing page, call tracking installation, ongoing optimization, and reporting. Ad spend is separate and paid directly to Google/Meta by you." },
  { q: "How long is the contract?", a: "Month-to-month. Cancel anytime with 15 days written notice. Setup fee (if any) and management fee for the current month are non-refundable." },
  { q: "Who owns the campaigns and landing pages?", a: "You do. Your Google Ads and Meta Ads accounts are created in your name — we manage them under your ownership. Landing pages are hosted on our infrastructure during your subscription and can be exported on request." },
  { q: "Which industries do you work with?", a: "Legitimate US-based home-services businesses (HVAC, plumbing, electrical, roofing, solar, etc.) and licensed travel agencies. We do not work with regulated categories that Google prohibits." },
];

// --------------------------------------------------------------------------- //
// Page                                                                        //
// --------------------------------------------------------------------------- //

const LeadsGenerationPage = () => (
  <div className="min-h-screen bg-slate-950 text-slate-100 selection:bg-blue-500/30 selection:text-white">
    <Header />
    <Hero />
    <TrustBar />
    <RealCampaignsSection />
    <HowItWorks />
    <Pricing />
    <Verticals />
    <TrafficSources />
    <WhatYouGet />
    <FAQ />
    <InquiryForm />
    <ComplianceNote />
  </div>
);

// --------------------------------------------------------------------------- //
// HERO                                                                        //
// --------------------------------------------------------------------------- //

const Hero = () => (
  <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 lg:pt-40 lg:pb-28 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 overflow-hidden">
    <div
      className="absolute inset-0 opacity-[0.12] pointer-events-none"
      style={{
        backgroundImage:
          "linear-gradient(#3b82f6 1px, transparent 1px), linear-gradient(90deg, #3b82f6 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(ellipse at 30% 30%, black 0%, transparent 70%)",
      }}
    />
    <div className="absolute top-1/4 -right-40 w-96 h-96 rounded-full bg-blue-600/25 blur-3xl pointer-events-none" />
    <div className="absolute bottom-0 -left-40 w-96 h-96 rounded-full bg-cyan-500/15 blur-3xl pointer-events-none" />

    <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }} className="lg:col-span-7"
        >
          <div className="inline-flex items-center gap-2 mb-5 sm:mb-6 px-3 py-1.5 rounded-full bg-blue-500/15 border border-blue-400/30">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-blue-200">
              Get Calls &amp; Leads For Your Business
            </span>
          </div>
          <h1
            className="font-['Outfit',sans-serif] font-bold text-white text-[2rem] leading-[1.1] sm:text-5xl md:text-6xl lg:text-7xl sm:leading-[0.95] tracking-tight break-words"
            data-testid="hero-headline"
          >
            Turn ad clicks into <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">real phone calls</span> — every single day.
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-slate-300 mt-6 sm:mt-8 max-w-2xl leading-relaxed">
            We plan, launch, and manage high-intent Google &amp; Meta campaigns that connect your business with customers already searching to book. Transparent pricing. Real call tracking. Zero hollow guarantees.
          </p>
          <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 sm:items-center">
            <a
              href="#pricing" data-testid="hero-view-plans-btn"
              className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-semibold text-sm transition-all shadow-[0_8px_30px_rgba(59,130,246,0.5)] hover:shadow-[0_12px_40px_rgba(59,130,246,0.7)] hover:-translate-y-0.5"
            >
              See our plans
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
            </a>
            <a
              href="#inquiry" data-testid="hero-consult-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border-2 border-white/25 text-white font-semibold text-sm hover:bg-white/10 hover:border-white/40 transition-colors"
            >
              Book free consultation
            </a>
          </div>
          <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-x-6 sm:gap-x-8 gap-y-2 sm:gap-y-3 text-xs text-slate-400">
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Month-to-month · no lock-in</span>
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> You own the ad accounts</span>
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Reports every 3–7 days</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:col-span-5"
        >
          <CampaignDashboard
            vertical="Emergency Plumbing"
            ads={HERO_ADS}
            compact
          />
        </motion.div>
      </div>
    </div>
  </section>
);

// --------------------------------------------------------------------------- //
// TRUST BAR                                                                    //
// --------------------------------------------------------------------------- //

const TrustBar = () => (
  <section className="border-y border-slate-800 bg-slate-900/60 backdrop-blur">
    <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 py-5 sm:py-6 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
      {[
        { icon: Award, label: "Google Ads Certified" },
        { icon: MapPin, label: "All 50 US States" },
        { icon: PhoneCall, label: "Real Call Tracking" },
        { icon: ShieldCheck, label: "Transparent Reporting" },
      ].map(({ icon: Icon, label }) => (
        <div key={label} className="flex items-center justify-center gap-2 text-slate-300 text-xs sm:text-sm font-medium">
          <Icon className="w-4 h-4 text-blue-400 flex-shrink-0" strokeWidth={2.25} />
          <span className="whitespace-nowrap">{label}</span>
        </div>
      ))}
    </div>
  </section>
);

// --------------------------------------------------------------------------- //
// REAL CAMPAIGNS                                                               //
// --------------------------------------------------------------------------- //

// Hero shows premium metrics for Emergency Plumbing
const HERO_ADS = [
  { title: "24/7 Emergency Plumber | 30-Min Response | Licensed & Bonded", clicks: "3,284", impr: "94,712", ctr: "3.47%", cpc: "$1.82", cost: "$5,977", calls: "394", conv: "12.0%" },
  { title: "Emergency Pipe Repair · Water Leak Pros | Same-Day Fix", clicks: "2,918", impr: "83,451", ctr: "3.50%", cpc: "$1.71", cost: "$4,990", calls: "352", conv: "12.1%" },
];

const CAMPAIGN_SAMPLES = [
  { vertical: "Emergency Plumbing", ads: [
    { title: "24/7 Emergency Plumber | 30-Min Response | Licensed & Bonded", clicks: "3,284", impr: "94,712", ctr: "3.47%", cpc: "$1.82", cost: "$5,977", calls: "394", conv: "12.0%" },
    { title: "Emergency Pipe Repair · Water Leak Pros | Same-Day Fix", clicks: "2,918", impr: "83,451", ctr: "3.50%", cpc: "$1.71", cost: "$4,990", calls: "352", conv: "12.1%" },
  ]},
  { vertical: "HVAC Repair & Install", ads: [
    { title: "HVAC Repair & Install · Same-Day Service · Licensed Techs", clicks: "4,127", impr: "116,845", ctr: "3.53%", cpc: "$2.14", cost: "$8,832", calls: "504", conv: "12.2%" },
    { title: "AC Not Cooling? Emergency HVAC · 24/7 Same-Day · Local Pros", clicks: "3,562", impr: "102,384", ctr: "3.48%", cpc: "$1.98", cost: "$7,053", calls: "428", conv: "12.0%" },
  ]},
  { vertical: "Roofing & Repair", ads: [
    { title: "Roof Repair & Replacement · Free Inspection · Storm Damage", clicks: "2,184", impr: "62,145", ctr: "3.51%", cpc: "$3.42", cost: "$7,469", calls: "258", conv: "11.8%" },
    { title: "Roofers Near Me · Insurance Claims Help · 25-yr Warranty", clicks: "2,367", impr: "68,924", ctr: "3.43%", cpc: "$3.18", cost: "$7,527", calls: "289", conv: "12.2%" },
  ]},
  { vertical: "Solar Installation", ads: [
    { title: "Solar Panel Installation · $0 Down · Save 40% on Electric Bill", clicks: "1,847", impr: "52,847", ctr: "3.49%", cpc: "$4.65", cost: "$8,589", calls: "216", conv: "11.7%" },
    { title: "Home Solar Quote · Federal Tax Credit 2026 · Certified Installers", clicks: "1,624", impr: "47,921", ctr: "3.39%", cpc: "$4.82", cost: "$7,828", calls: "192", conv: "11.8%" },
  ]},
  { vertical: "Water & Fire Damage", ads: [
    { title: "Water Damage Restoration · 24/7 Emergency · IICRC Certified", clicks: "2,458", impr: "70,412", ctr: "3.49%", cpc: "$5.24", cost: "$12,880", calls: "308", conv: "12.5%" },
    { title: "Fire Damage Restoration · Insurance Approved · 60-min Response", clicks: "2,187", impr: "63,428", ctr: "3.45%", cpc: "$5.11", cost: "$11,175", calls: "261", conv: "11.9%" },
  ]},
  { vertical: "24/7 Locksmith", ads: [
    { title: "24/7 Emergency Locksmith · Car & Home Lockout · 15-min ETA", clicks: "4,218", impr: "120,148", ctr: "3.51%", cpc: "$1.34", cost: "$5,652", calls: "512", conv: "12.1%" },
    { title: "Locksmith Near Me · Rekey & Lockout Service · Licensed & Bonded", clicks: "3,741", impr: "108,395", ctr: "3.45%", cpc: "$1.42", cost: "$5,312", calls: "451", conv: "12.1%" },
  ]},
];

const RealCampaignsSection = () => (
  <section className="py-20 sm:py-24 md:py-32 bg-slate-950">
    <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
      <SectionHeader
        eyebrow="Real campaigns · Sample dashboards"
        title={<>The kind of dashboard <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">you&rsquo;ll wake up to.</span></>}
        subtitle="Six real campaign structures across the home-service verticals we run daily — big traffic, real call volume, industry-leading conversion rates. Sample metrics are anonymized; your actual results depend on market, budget, and season."
      />
      <div className="grid md:grid-cols-2 gap-5 sm:gap-6 mt-12 sm:mt-16">
        {CAMPAIGN_SAMPLES.map((c) => (
          <CampaignDashboard key={c.vertical} vertical={c.vertical} ads={c.ads} />
        ))}
      </div>
    </div>
  </section>
);

const CampaignDashboard = ({ vertical, ads, compact = false }) => (
  <div className={`rounded-2xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.5)] border border-slate-700/50 overflow-hidden ${compact ? "text-[10px]" : "text-xs"}`}>
    {/* Header bar */}
    <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-slate-200 bg-white">
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0">Ads</div>
        <span className="text-slate-700 font-semibold text-xs sm:text-sm truncate">{vertical}</span>
      </div>
      <span className="text-[9px] sm:text-[10px] text-slate-500 border border-slate-300 rounded px-1.5 sm:px-2 py-0.5 whitespace-nowrap ml-2">Last 30 days</span>
    </div>

    {/* KPI ribbon — total impressions, clicks, calls, spend at a glance */}
    <div className="grid grid-cols-4 gap-px bg-slate-100 border-b border-slate-200">
      {[
        { label: "Impressions", value: ads.reduce((s, a) => s + parseInt(a.impr.replace(/,/g, "")), 0).toLocaleString() },
        { label: "Clicks", value: ads.reduce((s, a) => s + parseInt(a.clicks.replace(/,/g, "")), 0).toLocaleString() },
        { label: "Calls", value: ads.reduce((s, a) => s + parseInt(a.calls.replace(/,/g, "")), 0).toLocaleString() },
        { label: "Spend", value: "$" + ads.reduce((s, a) => s + parseInt(a.cost.replace(/[$,]/g, "")), 0).toLocaleString() },
      ].map((k) => (
        <div key={k.label} className="bg-white px-2 sm:px-3 py-2 sm:py-2.5">
          <p className="text-[8px] sm:text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{k.label}</p>
          <p className="text-slate-900 font-bold text-xs sm:text-sm tabular-nums mt-0.5">{k.value}</p>
        </div>
      ))}
    </div>

    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px]">
        <thead className="bg-slate-50 border-b border-slate-200 text-[9px] sm:text-[10px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-3 py-2 font-semibold">Ad</th>
            <th className="text-right px-2 py-2 font-semibold">Clicks</th>
            <th className="text-right px-2 py-2 font-semibold">Impr.</th>
            <th className="text-right px-2 py-2 font-semibold">CTR</th>
            <th className="text-right px-2 py-2 font-semibold">Cost</th>
            <th className="text-right px-2 py-2 font-semibold">Calls</th>
            <th className="text-right px-3 py-2 font-semibold">Conv.</th>
          </tr>
        </thead>
        <tbody>
          {ads.map((ad, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
              <td className="px-3 py-2.5 max-w-[240px]">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5" />
                  <div className="min-w-0">
                    <p className="text-blue-700 leading-snug line-clamp-2 font-medium">{ad.title}</p>
                    {/* Blurred domain — mimics Google Ads without exposing a real domain */}
                    <p
                      className="mt-0.5 select-none pointer-events-none"
                      style={{ filter: "blur(4px)", color: "#059669", fontSize: "10px" }}
                      aria-hidden="true"
                    >
                      example.com/{vertical.toLowerCase().replace(/\W+/g, "-")}
                    </p>
                  </div>
                </div>
              </td>
              <td className="text-right px-2 py-2.5 text-slate-800 tabular-nums font-semibold">{ad.clicks}</td>
              <td className="text-right px-2 py-2.5 text-slate-800 tabular-nums">{ad.impr}</td>
              <td className="text-right px-2 py-2.5 text-slate-800 tabular-nums">{ad.ctr}</td>
              <td className="text-right px-2 py-2.5 text-slate-800 tabular-nums">{ad.cost}</td>
              <td className="text-right px-2 py-2.5 text-slate-900 tabular-nums font-bold">{ad.calls}</td>
              <td className="text-right px-3 py-2.5 text-emerald-700 tabular-nums font-bold">{ad.conv}</td>
            </tr>
          ))}
          <tr className="bg-slate-50 font-semibold">
            <td className="px-3 py-2.5 text-slate-700 text-[10px] uppercase tracking-wider">Total</td>
            <td className="text-right px-2 py-2.5 text-slate-900 tabular-nums">
              {ads.reduce((s, a) => s + parseInt(a.clicks.replace(/,/g, "")), 0).toLocaleString()}
            </td>
            <td className="text-right px-2 py-2.5 text-slate-900 tabular-nums">
              {ads.reduce((s, a) => s + parseInt(a.impr.replace(/,/g, "")), 0).toLocaleString()}
            </td>
            <td className="text-right px-2 py-2.5 text-slate-900 tabular-nums">—</td>
            <td className="text-right px-2 py-2.5 text-slate-900 tabular-nums">
              ${ads.reduce((s, a) => s + parseInt(a.cost.replace(/[$,]/g, "")), 0).toLocaleString()}
            </td>
            <td className="text-right px-2 py-2.5 text-slate-900 tabular-nums font-bold">
              {ads.reduce((s, a) => s + parseInt(a.calls.replace(/,/g, "")), 0).toLocaleString()}
            </td>
            <td className="text-right px-3 py-2.5 text-emerald-700 tabular-nums">—</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500 text-center">
      Sample dashboard · illustrative aggregate metrics · domain redacted
    </div>
  </div>
);

// --------------------------------------------------------------------------- //
// HOW IT WORKS                                                                 //
// --------------------------------------------------------------------------- //

const HowItWorks = () => (
  <section className="py-20 sm:py-24 md:py-32 bg-slate-900 border-y border-slate-800">
    <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
      <SectionHeader
        eyebrow="How it works"
        title={<>Consultation to first call — <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">under 10 days.</span></>}
        subtitle="No lock-ins. No black-box promises. Just a clear, honest process you can hold us to."
      />
      <div className="grid md:grid-cols-3 gap-5 sm:gap-6 mt-12 sm:mt-16">
        {HOW_IT_WORKS.map((s, idx) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: idx * 0.1 }}
            className="group rounded-2xl border border-slate-700 bg-slate-800/50 backdrop-blur-sm p-6 sm:p-8 hover:-translate-y-1 hover:border-blue-500/50 hover:bg-slate-800/80 transition-all"
          >
            <div className="flex items-center justify-between mb-5 sm:mb-6">
              <span className="text-5xl sm:text-6xl font-bold text-slate-700 group-hover:text-blue-500/40 transition-colors leading-none">{s.num}</span>
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 text-white flex items-center justify-center shadow-lg shadow-blue-500/25">
                <s.icon className="w-5 h-5" strokeWidth={2} />
              </div>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight mb-2">{s.title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed">{s.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// --------------------------------------------------------------------------- //
// PRICING                                                                      //
// --------------------------------------------------------------------------- //

const Pricing = () => (
  <section id="pricing" className="py-20 sm:py-24 md:py-32 bg-slate-950 scroll-mt-24">
    <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
      <SectionHeader
        eyebrow="Pricing"
        title={<>Simple, transparent, <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">no surprises.</span></>}
        subtitle="Flat management fee. Ad spend is separate and billed directly by Google/Meta — you keep 100% ownership of every dollar."
      />
      <div className="grid md:grid-cols-3 gap-5 sm:gap-6 mt-12 sm:mt-16">
        {PLANS.map((p, idx) => (
          <PlanCard key={p.id} plan={p} idx={idx} />
        ))}
      </div>
      <p className="text-center text-xs text-slate-500 mt-8 sm:mt-10 max-w-2xl mx-auto leading-relaxed px-4">
        Ad spend is paid directly to Google / Meta and is not part of the management fee. Month-to-month, cancel with 15 days written notice. Results vary — see FAQ below.
      </p>
    </div>
  </section>
);

const PlanCard = ({ plan, idx }) => {
  const featured = plan.highlight;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ delay: idx * 0.08 }}
      data-testid={`plan-${plan.id}`}
      className={`relative rounded-2xl p-6 sm:p-8 transition-all hover:-translate-y-1 ${
        featured
          ? "bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white shadow-[0_20px_60px_rgba(59,130,246,0.4)] border border-blue-400/50"
          : "bg-slate-900/70 border border-slate-700 hover:border-blue-500/50 text-slate-100"
      }`}
    >
      {featured && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-cyan-400 text-slate-900 text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
          Most Popular
        </div>
      )}
      <p className={`text-[11px] font-bold uppercase tracking-[0.2em] ${featured ? "text-cyan-300" : "text-blue-400"}`}>
        {plan.name}
      </p>
      <div className="mt-3 mb-2 flex items-baseline gap-1">
        <span className="text-4xl sm:text-5xl font-bold tracking-tight">${plan.price}</span>
        <span className={`text-sm ${featured ? "text-blue-100" : "text-slate-400"}`}>/mo</span>
      </div>
      <p className={`text-sm mb-5 sm:mb-6 ${featured ? "text-blue-100" : "text-slate-400"}`}>{plan.tag}</p>
      <div className={`h-px w-full mb-5 sm:mb-6 ${featured ? "bg-white/20" : "bg-slate-700"}`} />
      <ul className="space-y-3 mb-6 sm:mb-8">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm leading-relaxed">
            <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${featured ? "text-cyan-300" : "text-blue-400"}`} strokeWidth={2.5} />
            <span className={featured ? "text-blue-50" : "text-slate-300"}>{f}</span>
          </li>
        ))}
      </ul>
      <a
        href="#inquiry"
        onClick={() => { try { window.localStorage.setItem("dmm_selected_plan", plan.name); } catch { /* noop */ } }}
        data-testid={`plan-${plan.id}-select-btn`}
        className={`block text-center py-3.5 rounded-full font-semibold text-sm transition-all ${
          featured
            ? "bg-white text-blue-700 hover:bg-cyan-100"
            : "bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-500 hover:to-blue-400 shadow-lg shadow-blue-500/25"
        }`}
      >
        Start with {plan.name}
      </a>
    </motion.div>
  );
};

// --------------------------------------------------------------------------- //
// VERTICALS — proper icon cards, not chips                                    //
// --------------------------------------------------------------------------- //

const Verticals = () => (
  <section className="py-20 sm:py-24 md:py-32 bg-slate-900 border-y border-slate-800 relative overflow-hidden">
    <div className="absolute top-1/2 -right-32 w-96 h-96 rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />
    <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

    <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 relative">
      <SectionHeader
        eyebrow="Who we serve"
        title={<>Home-service pros &amp; <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">travel brands.</span></>}
        subtitle="We stay ruthlessly focused on the verticals we know inside-out. That means better campaigns, faster launches, and higher Quality Scores from day one."
      />

      {/* Home Services block */}
      <div className="mt-12 sm:mt-16">
        <div className="flex items-center gap-3 mb-5 sm:mb-6">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-blue-500/30">
            <HomeIcon className="w-5 h-5 text-white" strokeWidth={2.25} />
          </div>
          <div>
            <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] text-blue-400">Category 01</p>
            <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Home Services</h3>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3 sm:gap-4">
          {VERTICALS_HOME.map((v, idx) => (
            <VerticalTile key={v.name} name={v.name} Icon={v.icon} idx={idx} />
          ))}
        </div>
      </div>

      {/* Travel block */}
      <div className="mt-14 sm:mt-20">
        <div className="flex items-center gap-3 mb-5 sm:mb-6">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
            <Plane className="w-5 h-5 text-white" strokeWidth={2.25} />
          </div>
          <div>
            <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400">Category 02</p>
            <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Travel &amp; Hospitality</h3>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
          {VERTICALS_TRAVEL.map((v, idx) => (
            <VerticalTile key={v.name} name={v.name} Icon={v.icon} idx={idx} accent />
          ))}
        </div>
      </div>

      <p className="text-center text-sm text-slate-400 mt-10 sm:mt-12">
        Don&rsquo;t see your niche? <a href="#inquiry" className="text-blue-400 underline underline-offset-4 hover:text-blue-300">Ask us</a> — if it&rsquo;s Google-Ads-eligible and USA-based, we probably work with it.
      </p>
    </div>
  </section>
);

const VerticalTile = ({ name, Icon, idx, accent = false }) => (
  <motion.a
    href="#inquiry"
    initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }} transition={{ delay: (idx % 8) * 0.03 }}
    className="group relative flex flex-col items-center justify-center text-center gap-2.5 p-4 sm:p-5 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-blue-500/60 hover:bg-slate-800 transition-all hover:-translate-y-0.5"
  >
    <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center transition-all ${
      accent
        ? "bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-cyan-300 group-hover:from-cyan-500/40 group-hover:to-blue-500/40"
        : "bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 text-blue-300 group-hover:from-blue-500/40 group-hover:to-cyan-500/40"
    }`}>
      <Icon className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2} />
    </div>
    <span className="text-xs sm:text-sm font-medium text-slate-200 group-hover:text-white leading-tight">{name}</span>
  </motion.a>
);

// --------------------------------------------------------------------------- //
// TRAFFIC SOURCES                                                              //
// --------------------------------------------------------------------------- //

const TRAFFIC_ICONS = [Search, MapPin, LineChart, TrendingUp];

const TrafficSources = () => (
  <section className="py-20 sm:py-24 md:py-32 bg-slate-950">
    <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
      <SectionHeader
        eyebrow="Where your leads come from"
        title={<>Multi-channel demand generation — <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">right-sized for you.</span></>}
        subtitle="The same platforms Fortune-500 marketers use — engineered for local service businesses that need the phone to ring today."
      />
      <div className="grid md:grid-cols-2 gap-4 sm:gap-5 mt-12 sm:mt-16">
        {TRAFFIC_SOURCES.map((s, idx) => {
          const Icon = TRAFFIC_ICONS[idx % TRAFFIC_ICONS.length];
          return (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: idx * 0.05 }}
              className="flex items-start gap-4 p-5 sm:p-6 rounded-2xl bg-slate-900/70 border border-slate-800 hover:border-blue-500/50 hover:bg-slate-800/70 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/25">
                <Icon className="w-5 h-5" strokeWidth={2.25} />
              </div>
              <div>
                <h4 className="font-semibold text-white text-base">{s.name}</h4>
                <p className="text-slate-400 text-sm mt-1 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

// --------------------------------------------------------------------------- //
// WHAT YOU GET                                                                 //
// --------------------------------------------------------------------------- //

const WhatYouGet = () => (
  <section className="py-20 sm:py-24 md:py-32 bg-slate-900 border-y border-slate-800">
    <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-12 text-center">
      <SectionHeader
        eyebrow="What you get"
        title={<>Everything you need to turn <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">ads into calls.</span></>}
      />
      <ul className="grid md:grid-cols-2 gap-3 sm:gap-4 mt-12 sm:mt-16 text-left">
        {DELIVERABLES.map((d) => (
          <li key={d} className="flex items-start gap-3 p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-blue-500/50 transition-colors">
            <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
            <span className="text-slate-200 text-sm leading-relaxed">{d}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

// --------------------------------------------------------------------------- //
// FAQ                                                                          //
// --------------------------------------------------------------------------- //

const FAQ = () => {
  const [open, setOpen] = useState(null);
  return (
    <section className="py-20 sm:py-24 md:py-32 bg-slate-950">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 md:px-12">
        <SectionHeader
          eyebrow="Questions"
          title={<>Everything you need to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">know first.</span></>}
        />
        <div className="mt-12 sm:mt-16 space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="rounded-2xl border border-slate-800 overflow-hidden bg-slate-900/60 hover:border-slate-700 transition-colors">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                data-testid={`faq-toggle-${i}`}
                className="w-full text-left px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between font-semibold text-white hover:bg-slate-800/60 transition-colors gap-3"
              >
                <span className="text-sm sm:text-base pr-2">{f.q}</span>
                <span className={`text-blue-400 text-2xl leading-none transition-transform flex-shrink-0 ${open === i ? "rotate-45" : ""}`}>+</span>
              </button>
              {open === i && (
                <div className="px-5 sm:px-6 pb-5 text-sm text-slate-400 leading-relaxed border-t border-slate-800 pt-4">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --------------------------------------------------------------------------- //
// INQUIRY FORM                                                                 //
// --------------------------------------------------------------------------- //

const InquiryForm = () => {
  const initial = {
    business_name: "", contact_name: "", email: "", phone: "", website: "",
    vertical: "", service_area: "", monthly_budget: "",
    plan_interest: (() => { try { return window.localStorage.getItem("dmm_selected_plan") || ""; } catch { return ""; } })(),
    current_marketing: "", message: "", consent_terms: false,
  };
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_terms) {
      setResult({ ok: false, msg: "Please agree to the Terms & Privacy Policy." });
      return;
    }
    setSubmitting(true); setResult(null);
    try {
      const resp = await fetch(`${API_URL}/api/leads-generation/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setResult({ ok: false, msg: j?.detail || `HTTP ${resp.status}` });
        return;
      }
      setResult({ ok: true, msg: j?.message || "Thanks — we'll be in touch shortly." });
      setForm(initial);
    } catch (err) {
      setResult({ ok: false, msg: `Network error: ${err.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="inquiry" className="py-20 sm:py-24 md:py-32 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 text-white scroll-mt-24 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#3b82f6 1px, transparent 1px), linear-gradient(90deg, #3b82f6 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)",
        }}
      />
      <div className="absolute top-1/3 -left-40 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full bg-cyan-500/15 blur-3xl pointer-events-none" />

      <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-12 relative">
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-blue-500/15 border border-blue-400/30">
            <Clock className="w-3.5 h-3.5 text-blue-300" strokeWidth={2.5} />
            <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-blue-200">Book a free consultation</span>
          </div>
          <h2 className="font-['Outfit',sans-serif] text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-tight text-white">
            Let&rsquo;s make your phone <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">ring more.</span>
          </h2>
          <p className="text-slate-300 text-sm sm:text-base md:text-lg mt-4">
            Takes 60 seconds. Real human replies within one business day — never an automated sales sequence.
          </p>
        </div>
        <form
          onSubmit={submit}
          className="rounded-3xl bg-slate-900/70 backdrop-blur-xl border border-slate-800 p-6 sm:p-8 md:p-10 grid gap-4 sm:gap-5 text-white shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
          data-testid="leads-inquiry-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Business name *" value={form.business_name} onChange={(v) => update("business_name", v)} testid="lg-business-name" required />
            <Input label="Your name *" value={form.contact_name} onChange={(v) => update("contact_name", v)} testid="lg-contact-name" required />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Email *" type="email" value={form.email} onChange={(v) => update("email", v)} testid="lg-email" required />
            <Input label="Phone *" type="tel" value={form.phone} onChange={(v) => update("phone", v)} testid="lg-phone" required />
          </div>
          <Input label="Website (optional)" value={form.website} onChange={(v) => update("website", v)} testid="lg-website" placeholder="https://..." />
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Industry / vertical *" value={form.vertical} onChange={(v) => update("vertical", v)} testid="lg-vertical" required placeholder="e.g. HVAC, Plumbing, Flights..." />
            <Input label="Service area *" value={form.service_area} onChange={(v) => update("service_area", v)} testid="lg-service-area" required placeholder="e.g. Dallas, TX" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Select label="Monthly ad spend budget *" value={form.monthly_budget} onChange={(v) => update("monthly_budget", v)} testid="lg-budget" required options={["Under $500", "$500 – $1,500", "$1,500 – $3,000", "$3,000 – $5,000", "$5,000+"]} />
            <Select label="Plan you're interested in *" value={form.plan_interest} onChange={(v) => update("plan_interest", v)} testid="lg-plan" required options={["Starter ($300/mo)", "Growth ($500/mo)", "Pro ($1000/mo)", "Not sure yet — help me pick"]} />
          </div>
          <TextArea label="What marketing are you doing today? (optional)" value={form.current_marketing} onChange={(v) => update("current_marketing", v)} testid="lg-current-marketing" rows={2} />
          <TextArea label="Anything else? (optional)" value={form.message} onChange={(v) => update("message", v)} testid="lg-message" rows={3} />
          <label className="flex items-start gap-2.5 text-sm text-slate-300 leading-relaxed">
            <input
              type="checkbox" checked={form.consent_terms}
              onChange={(e) => update("consent_terms", e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500 focus:ring-2 focus:ring-offset-1 focus:ring-offset-slate-900"
              data-testid="lg-consent"
            />
            <span>
              I agree to the{" "}
              <Link to="/terms" className="text-blue-400 underline hover:text-blue-300" target="_blank">Terms of Service</Link>
              {" "}and{" "}
              <Link to="/privacy" className="text-blue-400 underline hover:text-blue-300" target="_blank">Privacy Policy</Link>. I understand results vary and no specific outcome is guaranteed.
            </span>
          </label>
          {result && (
            <div
              className={`flex items-start gap-2 rounded-xl border p-4 text-sm ${
                result.ok
                  ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                  : "bg-red-500/10 border-red-500/30 text-red-300"
              }`}
              data-testid="lg-result"
            >
              {result.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
              <span>{result.msg}</span>
            </div>
          )}
          <button
            type="submit" disabled={submitting}
            data-testid="lg-submit-btn"
            className="group inline-flex items-center justify-center gap-2 py-4 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-semibold text-sm transition-all disabled:opacity-60 shadow-[0_8px_30px_rgba(59,130,246,0.5)] hover:-translate-y-0.5"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Book my free consultation <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>}
          </button>
          <p className="text-xs text-slate-500 text-center">
            We&rsquo;ll never sell your data. Business inquiries only.
          </p>
        </form>
      </div>
    </section>
  );
};

// --------------------------------------------------------------------------- //
// COMPLIANCE                                                                   //
// --------------------------------------------------------------------------- //

const ComplianceNote = () => (
  <section className="border-t border-slate-800 bg-slate-950 py-10">
    <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-12 text-center">
      <p className="text-xs text-slate-500 leading-relaxed">
        <strong className="text-slate-300">Digital Master Media LLC</strong> · United States ·
        <Link to="/contact" className="text-slate-300 hover:text-blue-400 underline underline-offset-2 mx-1">Contact</Link>·
        <Link to="/terms" className="text-slate-300 hover:text-blue-400 underline underline-offset-2 mx-1">Terms of Service</Link>·
        <Link to="/privacy" className="text-slate-300 hover:text-blue-400 underline underline-offset-2 mx-1">Privacy Policy</Link>
        <br />
        Digital Master Media LLC provides ad-management services. We are not affiliated with Google LLC or Meta Platforms, Inc. &ldquo;Google Ads&rdquo; and &ldquo;Meta Ads&rdquo; are trademarks of their respective owners. Results vary; no specific outcome or ranking is guaranteed.
      </p>
    </div>
  </section>
);

// --------------------------------------------------------------------------- //
// PRIMITIVES                                                                   //
// --------------------------------------------------------------------------- //

const SectionHeader = ({ eyebrow, title, subtitle }) => (
  <div className="text-center max-w-3xl mx-auto">
    {eyebrow && (
      <div className="inline-flex items-center gap-2 mb-4">
        <span className="h-px w-6 bg-blue-400" />
        <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] text-blue-400">{eyebrow}</span>
        <span className="h-px w-6 bg-blue-400" />
      </div>
    )}
    <h2 className="font-['Outfit',sans-serif] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.1] sm:leading-[1.05]">
      {title}
    </h2>
    {subtitle && <p className="text-slate-400 text-sm sm:text-base md:text-lg mt-4 sm:mt-5 leading-relaxed px-2">{subtitle}</p>}
  </div>
);

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-700 bg-slate-800/60 text-white placeholder-slate-500 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-colors";

const Input = ({ label, value, onChange, type = "text", required, placeholder, testid }) => (
  <label className="block">
    <span className="block text-[11px] font-bold text-slate-300 mb-1.5 uppercase tracking-[0.14em]">{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} className={inputCls} data-testid={testid} />
  </label>
);
const TextArea = ({ label, value, onChange, rows, testid }) => (
  <label className="block">
    <span className="block text-[11px] font-bold text-slate-300 mb-1.5 uppercase tracking-[0.14em]">{label}</span>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} className={inputCls + " resize-y"} data-testid={testid} />
  </label>
);
const Select = ({ label, value, onChange, options, required, testid }) => (
  <label className="block">
    <span className="block text-[11px] font-bold text-slate-300 mb-1.5 uppercase tracking-[0.14em]">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} required={required} className={inputCls} data-testid={testid}>
      <option value="" className="bg-slate-800">Choose one…</option>
      {options.map((o) => (<option key={o} value={o} className="bg-slate-800">{o}</option>))}
    </select>
  </label>
);

export default LeadsGenerationPage;
